// src/pages/Orders/RefundProcessing.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getOrderByIdForAdminThunk,
  completeRefundThunk,
} from "../../store/thunks/orderThunks";
import Button from "../../components/common/Button";
import Badge from "../../components/common/Badge";
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  RefreshIcon,
  ExclamationCircleIcon,
  UserIcon,
  CurrencyRupeeIcon,
  DocumentTextIcon,
  ClockIcon,
} from "@heroicons/react/outline";
import { notifySuccess, notifyError } from "../../utils/notification";

const RefundProcessing = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { order = null, loading = false } = useSelector(
    (state) => state.orders || {}
  );

  const [formData, setFormData] = useState({
    transactionId: "",
    referenceNumber: "",
    notes: "",
    internalNotes: "",
    completedAt: new Date().toISOString().slice(0, 16), // datetime-local format
  });

  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (orderId) {
      dispatch(getOrderByIdForAdminThunk(orderId));
    }
  }, [orderId, dispatch]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.transactionId.trim()) {
      notifyError("Please provide a transaction ID");
      return;
    }

    if (!formData.referenceNumber.trim()) {
      notifyError("Please provide a reference number");
      return;
    }

    if (
      !window.confirm(
        "Are you sure you want to complete this refund? This action cannot be undone."
      )
    ) {
      return;
    }

    setProcessing(true);
    try {
      await dispatch(
        completeRefundThunk({
          orderId: order.orderId,
          transactionId: formData.transactionId,
          referenceNumber: formData.referenceNumber,
          notes: formData.notes,
          internalNotes: formData.internalNotes,
          completedAt: formData.completedAt,
        })
      ).unwrap();

      notifySuccess("Refund completed successfully");
      navigate(`/orders/${orderId}`);
    } catch (error) {
      notifyError(error || "Failed to complete refund");
      setProcessing(false);
    }
  };

  if (loading || !order) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const refund = order.refundDetails;

  if (!refund || !refund.required) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <ExclamationCircleIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            No Refund Required
          </h2>
          <p className="text-gray-600 mb-4">
            This order does not require a refund.
          </p>
          <Button onClick={() => navigate(`/orders/${orderId}`)}>
            Back to Order
          </Button>
        </div>
      </div>
    );
  }

  if (
    refund.refundStatus !== "REFUND_PENDING" &&
    refund.refundStatus !== "REFUND_APPROVED"
  ) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Refund Already {refund.refundStatus}
          </h2>
          <p className="text-gray-600 mb-4">
            This refund has already been processed.
          </p>
          <Button onClick={() => navigate(`/orders/${orderId}`)}>
            Back to Order
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/orders/${orderId}`)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to Order Details
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 bg-green-100 rounded-lg">
            <CurrencyRupeeIcon className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Process Refund - {order.orderId}
            </h1>
            <p className="text-sm text-gray-600">
              Complete refund transaction details
            </p>
          </div>
        </div>
      </div>

      {/* Info Alert */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
        <div className="flex">
          <ExclamationCircleIcon className="w-5 h-5 text-blue-400 mr-3 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-blue-800">
              Important Information
            </p>
            <p className="text-sm text-blue-700 mt-1">
              Please ensure you have completed the refund transaction in your
              payment gateway/bank before marking it as complete here. Enter the
              transaction details below for record-keeping.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Refund Summary Card */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4">
            <h2 className="text-lg font-semibold text-white">Refund Summary</h2>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Refund Amount */}
              <div className="md:col-span-2 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-6 border-2 border-green-200">
                <p className="text-sm text-green-700 mb-1">
                  Amount to be Refunded
                </p>
                <p className="text-4xl font-bold text-green-600">
                  {formatCurrency(refund.refundAmount)}
                </p>
              </div>

              {/* Refund Details */}
              <div>
                <p className="text-sm text-gray-600 mb-1">Refund Method</p>
                <p className="font-semibold text-gray-900">
                  {refund.refundMethod}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-600 mb-1">Current Status</p>
                <Badge
                  variant={
                    refund.refundStatus === "APPROVED" ? "blue" : "orange"
                  }
                  size="lg"
                >
                  {refund.refundStatus}
                </Badge>
              </div>

              <div>
                <p className="text-sm text-gray-600 mb-1">Refund Reason</p>
                <p className="font-semibold text-gray-900">
                  {refund.refundReason}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-600 mb-1">Initiated At</p>
                <p className="font-semibold text-gray-900">
                  {formatDate(refund.initiatedAt)}
                </p>
              </div>

              {refund.approvalDate && (
                <>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Approved By</p>
                    <p className="font-semibold text-gray-900">
                      {refund.approvedBy}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Approved At</p>
                    <p className="font-semibold text-gray-900">
                      {formatDate(refund.approvalDate)}
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Bank/UPI Details */}
            {refund.refundMethod === "BANK_TRANSFER" && refund.bankDetails && (
              <div className="mt-6 pt-6 border-t">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <DocumentTextIcon className="w-4 h-4" />
                  Bank Account Details
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-600">Account Name</p>
                    <p className="font-medium text-gray-900">
                      {refund.bankDetails.accountName}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Account Number</p>
                    <p className="font-medium text-gray-900">
                      {refund.bankDetails.accountNumber}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">IFSC Code</p>
                    <p className="font-medium text-gray-900">
                      {refund.bankDetails.ifscCode}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Bank Name</p>
                    <p className="font-medium text-gray-900">
                      {refund.bankDetails.bankName}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {refund.refundMethod === "UPI" && refund.upiDetails && (
              <div className="mt-6 pt-6 border-t">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  UPI Details
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-600">UPI ID</p>
                      <p className="font-medium text-gray-900">
                        {refund.upiDetails.vpa}
                      </p>
                    </div>
                    {refund.upiDetails.verifiedName && (
                      <div>
                        <p className="text-xs text-gray-600">Verified Name</p>
                        <p className="font-medium text-gray-900">
                          {refund.upiDetails.verifiedName}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Customer Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <UserIcon className="w-5 h-5" />
            Customer Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Name</p>
              <p className="font-semibold text-gray-900">
                {order.userId?.name || order.guestCustomer?.name || "Guest"}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Phone</p>
              <p className="font-semibold text-gray-900">
                {order.userId?.phoneNumber ||
                  order.guestCustomer?.phoneNumber ||
                  "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Email</p>
              <p className="font-semibold text-gray-900">
                {order.userId?.email || order.guestCustomer?.email || "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Account Type</p>
              <p className="font-semibold text-gray-900">
                {order.userId?.accountType || "B2C"}
              </p>
            </div>
          </div>
        </div>

        {/* Transaction Details Form */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Transaction Details
          </h2>

          <div className="space-y-4">
            {/* Transaction ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transaction ID *
                <span className="text-gray-500 font-normal ml-1">
                  (from payment gateway)
                </span>
              </label>
              <input
                type="text"
                value={formData.transactionId}
                onChange={(e) =>
                  setFormData({ ...formData, transactionId: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter transaction ID from payment gateway"
                required
              />
            </div>

            {/* Reference Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reference Number *
                <span className="text-gray-500 font-normal ml-1">
                  (bank reference or UTR number)
                </span>
              </label>
              <input
                type="text"
                value={formData.referenceNumber}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    referenceNumber: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter bank reference or UTR number"
                required
              />
            </div>

            {/* Completion Date/Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Completion Date & Time *
              </label>
              <input
                type="datetime-local"
                value={formData.completedAt}
                onChange={(e) =>
                  setFormData({ ...formData, completedAt: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>

            {/* Customer Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes to Customer (Optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Add any notes for the customer (e.g., processing time, next steps)..."
              />
            </div>

            {/* Internal Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Internal Notes (Optional)
              </label>
              <textarea
                value={formData.internalNotes}
                onChange={(e) =>
                  setFormData({ ...formData, internalNotes: e.target.value })
                }
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Add internal notes (not visible to customer)..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Internal notes are only visible to staff members
              </p>
            </div>
          </div>
        </div>

        {/* Confirmation Checklist */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-yellow-900 mb-3 flex items-center gap-2">
            <ExclamationCircleIcon className="w-5 h-5" />
            Pre-Completion Checklist
          </h3>
          <div className="space-y-2 text-sm text-yellow-800">
            <div className="flex items-start gap-2">
              <CheckCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                Refund transaction has been completed in payment gateway/bank
              </span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                Transaction ID and reference number have been verified
              </span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Customer has been notified about the refund</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Bank/UPI details have been double-checked</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/orders/${orderId}`)}
              disabled={processing}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="success"
              disabled={processing}
              className="flex-1 flex items-center justify-center gap-2"
            >
              {processing ? (
                <>
                  <RefreshIcon className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="w-5 h-5" />
                  Complete Refund
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RefundProcessing;
